import { MerchantMessages, MerchantTeam } from 'containers/app/app.model';
import { ConnectBankStatuses, EnablePayoutStatuses, InternalBankStatuses } from 'containers/payouts/constants';

export enum KycLimitMessagingCase {
  NOT_SUBMITTED = 'NOT_SUBMITTED',
  BANK_NOT_CONNECTED = 'BANK_NOT_CONNECTED',
  UNDER_REVIEW = 'UNDER_REVIEW',
  NONE = 'NONE'
}

export interface KycLimitMessagingContent {
  cardHeader: string;
  cardBody: string;
  oneLiner: string;
  cta?: string;
}

const NOT_SUBMITTED_STATUSES: EnablePayoutStatuses[] = [
  EnablePayoutStatuses.NOT_STARTED,
  EnablePayoutStatuses.STARTED_IN_PROGRESS
];

const LIMIT_REVISION_MESSAGE_KEYS: Record<
  Exclude<KycLimitMessagingCase, KycLimitMessagingCase.NONE>,
  {
    oneLiner: string;
    oneLinerNonOwner: string;
    cardHeader: string;
    cardBody: string;
    cardBodyNonOwner: string;
    cta?: string;
  }
> = {
  [KycLimitMessagingCase.NOT_SUBMITTED]: {
    oneLiner: 'LIMIT_REVISION_KYC_NOT_SUBMITTED_ONE_LINER',
    oneLinerNonOwner: 'LIMIT_REVISION_KYC_NOT_SUBMITTED_ONE_LINER_NON_OWNER',
    cardHeader: 'LIMIT_REVISION_KYC_NOT_SUBMITTED_CARD_HEADER',
    cardBody: 'LIMIT_REVISION_KYC_NOT_SUBMITTED_CARD_BODY',
    cardBodyNonOwner: 'LIMIT_REVISION_KYC_NOT_SUBMITTED_CARD_BODY_NON_OWNER',
    cta: 'LIMIT_REVISION_KYC_NOT_SUBMITTED_CTA'
  },
  [KycLimitMessagingCase.BANK_NOT_CONNECTED]: {
    oneLiner: 'LIMIT_REVISION_BANK_NOT_CONNECTED_ONE_LINER',
    oneLinerNonOwner: 'LIMIT_REVISION_BANK_NOT_CONNECTED_ONE_LINER_NON_OWNER',
    cardHeader: 'LIMIT_REVISION_BANK_NOT_CONNECTED_CARD_HEADER',
    cardBody: 'LIMIT_REVISION_BANK_NOT_CONNECTED_CARD_BODY',
    cardBodyNonOwner: 'LIMIT_REVISION_BANK_NOT_CONNECTED_CARD_BODY_NON_OWNER',
    cta: 'LIMIT_REVISION_BANK_NOT_CONNECTED_CTA'
  },
  [KycLimitMessagingCase.UNDER_REVIEW]: {
    oneLiner: 'LIMIT_REVISION_UNDER_REVIEW_ONE_LINER',
    oneLinerNonOwner: 'LIMIT_REVISION_UNDER_REVIEW_ONE_LINER_NON_OWNER',
    cardHeader: 'LIMIT_REVISION_UNDER_REVIEW_CARD_HEADER',
    cardBody: 'LIMIT_REVISION_UNDER_REVIEW_CARD_BODY',
    cardBodyNonOwner: 'LIMIT_REVISION_UNDER_REVIEW_CARD_BODY_NON_OWNER'
  }
};

const isBankFullyConnected = (team: MerchantTeam): boolean => {
  return (
    team.connectBankingStatus === ConnectBankStatuses.SUBMITTED &&
    team.internalBankingStatus === InternalBankStatuses.FINANCIAL_DATA_VERIFIED
  );
};

export const resolveKycLimitMessagingCase = (team: MerchantTeam): KycLimitMessagingCase => {
  const connectStatus = team.connectApplicationStatus;

  if (NOT_SUBMITTED_STATUSES.includes(connectStatus)) {
    return KycLimitMessagingCase.NOT_SUBMITTED;
  }

  if (connectStatus === EnablePayoutStatuses.REJECTED) {
    return KycLimitMessagingCase.NONE;
  }

  if (!isBankFullyConnected(team)) {
    return KycLimitMessagingCase.BANK_NOT_CONNECTED;
  }

  return KycLimitMessagingCase.UNDER_REVIEW;
};

export const mapKycLimitMessagingContent = (
  messagingCase: KycLimitMessagingCase,
  merchantMessages: MerchantMessages,
  isOwner: boolean
): KycLimitMessagingContent | null => {
  if (messagingCase === KycLimitMessagingCase.NONE) {
    return null;
  }

  const keys = LIMIT_REVISION_MESSAGE_KEYS[messagingCase];
  const oneLinerKey = isOwner ? keys.oneLiner : keys.oneLinerNonOwner;
  const cardBodyKey = isOwner ? keys.cardBody : keys.cardBodyNonOwner;
  const oneLiner = merchantMessages[oneLinerKey];
  const cardHeader = merchantMessages[keys.cardHeader];
  const cardBody = merchantMessages[cardBodyKey];

  if (!oneLiner || !cardHeader || !cardBody) {
    return null;
  }

  const content: KycLimitMessagingContent = {
    oneLiner,
    cardHeader,
    cardBody
  };

  if (isOwner && keys.cta) {
    const cta = merchantMessages[keys.cta];
    if (cta) {
      content.cta = cta;
    }
  }

  return content;
};
